/**
 * One purchase request on /orders: which vehicle, where it is, and what the
 * customer has to do next — readable on a phone without opening the detail.
 */
import { Link } from 'react-router-dom';
import { StockImage } from '../stock/StockImage';
import { linkButtonStyle } from './linkButtonStyle';
import type { PurchaseRequest } from '../../lib/purchaseRequestsApi';
import {
  formatRequestDate,
  purchaseRequestStatus,
  requestNumber,
  vehicleTitle,
  type PurchaseRequestStatusView,
} from '../../lib/purchaseRequestStatus';

const TONE: Record<PurchaseRequestStatusView['tone'], { bg: string; color: string }> = {
  info: { bg: '#dbeafe', color: '#1e40af' },
  action: { bg: '#fef3c7', color: '#92400e' },
  success: { bg: '#d1fae5', color: '#065f46' },
  neutral: { bg: '#f3f4f6', color: '#374151' },
  danger: { bg: '#fee2e2', color: '#991b1b' },
};

export function PurchaseRequestStatusPill({ state }: { state: string }): JSX.Element {
  const view = purchaseRequestStatus(state);
  const tone = TONE[view.tone];
  return (
    <span
      data-testid="purchase-request-status"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '0.1875rem 0.625rem',
        borderRadius: '9999px',
        fontSize: '0.75rem',
        fontWeight: 700,
        whiteSpace: 'nowrap',
        background: tone.bg,
        color: tone.color,
      }}
    >
      {view.label}
    </span>
  );
}

export function PurchaseRequestCard({ request }: { request: PurchaseRequest }): JSX.Element {
  const view = purchaseRequestStatus(request.state);
  const title = vehicleTitle(request);
  const detailHref = `/orders/requests/${request.buyRequestId}`;
  return (
    <article
      data-testid={`purchase-request-card-${request.buyRequestId}`}
      aria-label={`${title} — ${view.label}`}
      style={{
        display: 'grid',
        gridTemplateRows: 'auto 1fr',
        border: `1px solid ${view.group === 'action' ? '#f59e0b' : '#e5e7eb'}`,
        borderRadius: '0.5rem',
        background: '#fff',
        overflow: 'hidden',
      }}
    >
      <StockImage
        src={request.vehicleImageUrlSnapshot ?? ''}
        alt={title}
        style={{ width: '100%', aspectRatio: '4 / 3', objectFit: 'cover', display: 'block' }}
      />
      <div style={{ display: 'grid', gap: '0.625rem', padding: '0.875rem 1rem 1rem' }}>
        <div>
          <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: '#111827' }}>
            {title}
          </h3>
          <p style={{ margin: '0.125rem 0 0', fontSize: '0.8125rem', color: '#4b5563' }}>
            {[
              request.vehicleGradeSnapshot,
              request.vehicleInventoryNoSnapshot
                ? `Stock No. ${request.vehicleInventoryNoSnapshot}`
                : null,
            ]
              .filter(Boolean)
              .join(' · ') || '—'}
          </p>
          <p style={{ margin: '0.125rem 0 0', fontSize: '0.75rem', color: '#6b7280' }}>
            {requestNumber(request.buyRequestId)} · Requested {formatRequestDate(request.createdAt)}
          </p>
        </div>
        <div>
          <PurchaseRequestStatusPill state={request.state} />
        </div>
        <p style={{ margin: 0, fontSize: '0.8125rem', color: '#111827' }}>
          <strong style={{ fontWeight: 700 }}>Next step: </strong>
          {view.nextAction}
        </p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '0.125rem' }}>
          {view.group === 'action' ? (
            <Link to="/invoices" style={linkButtonStyle('primary')}>
              Check invoice
            </Link>
          ) : null}
          <Link
            to={detailHref}
            style={linkButtonStyle(view.group === 'action' ? 'secondary' : 'primary')}
            data-testid={`purchase-request-link-${request.buyRequestId}`}
          >
            View details
          </Link>
        </div>
      </div>
    </article>
  );
}
